import { useState, useEffect } from "react";
import useWindowSize from "../hooks/useWindowSize";
import aitLogo from "../assets/ait.svg";
import secureAuthService from '../services/secureAuthService';
import '../styles/pages/LoginPage.css';

/* ═══════════════════════ ICON COMPONENTS ═══════════════════════ */
const EyeOpen = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeClosed = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="4" width="20" height="16" rx="3"/>
    <polyline points="22,7 12,14 2,7"/>
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="11" width="18" height="11" rx="3"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

/* ═══════════════════════ VALIDATORS ═══════════════════════ */
function validateEmail(v) {
  if (!v.trim()) return "Email is required";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "Enter a valid email address";
  return "";
}

function validatePassword(v) {
  if (!v) return "Password is required";
  if (v.length < 6) return "Password must be at least 6 characters";
  return "";
}

/* ═══════════════════════ COMPONENT ═══════════════════════ */
export default function LoginPage({ onLogin, onForgotPassword }) {
  const { isMobile } = useWindowSize();
  const [step, setStep] = useState("login"); // "login" or "newPassword"
  const [email, setEmail]           = useState("");
  const [password, setPassword]     = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw]         = useState(false);
  const [showNewPw, setShowNewPw]   = useState(false);
  const [showConfPw, setShowConfPw] = useState(false);
  const [emailErr, setEmailErr]     = useState("");
  const [pwErr, setPwErr]           = useState("");
  const [newPwErr, setNewPwErr]     = useState("");
  const [confPwErr, setConfPwErr]   = useState("");
  const [formErr, setFormErr]       = useState("");
  const [loading, setLoading]       = useState(false);
  const [touched, setTouched]       = useState({ email: false, pw: false });
  const [shake, setShake]           = useState(false);

  // Note: Session clearing is now handled by Dashboard.jsx's checkAuthStatus
  // We don't auto-clear on LoginPage mount to avoid "user already signed in" errors
  // If a valid session exists, Dashboard will redirect to the app automatically

  /* live validation after first touch */
  useEffect(() => { if (touched.email) setEmailErr(validateEmail(email)); }, [email, touched.email]);
  useEffect(() => { if (touched.pw)   setPwErr(validatePassword(password)); }, [password, touched.pw]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, pw: true });
    const eErr = validateEmail(email);
    const pErr = validatePassword(password);
    setEmailErr(eErr);
    setPwErr(pErr);
    if (eErr || pErr) return;

    setLoading(true);
    setFormErr("");

    try {
      const result = await secureAuthService.signIn(email, password);

      if (result.success) {
        const user = {
          email: result.user?.email || email.trim().toLowerCase(),
          name: result.user?.email?.split('@')[0] || email.split('@')[0],
          facilityId: result.user?.facility_id || ''
        };
        
        console.log('[LoginPage] Login successful, user:', user);
        onLogin(user);
      } else if (result.requiresNewPassword) {
        setStep("newPassword");
        setLoading(false);
      } else {
        setFormErr(result.message || "Authentication failed");
        setShake(true);
        setTimeout(() => setShake(false), 500);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error('Sign in error:', error);
    
      if (error.name === 'UserNotFoundException' || error.name === 'NotAuthorizedException') {
        setFormErr("Invalid email or password. Please try again.");
      } else if (error.name === 'UserNotConfirmedException') {
        setFormErr("Please verify your email address before signing in.");
      } else {
        setFormErr(error.message || "An error occurred during sign in.");
      }
    
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    let hasErr = false;

    if (!newPassword) { setNewPwErr("New password is required"); hasErr = true; }
    else if (newPassword.length < 8) { setNewPwErr("Password must be at least 8 characters"); hasErr = true; }
    else if (!/[A-Z]/.test(newPassword)) { setNewPwErr("Include at least one uppercase letter"); hasErr = true; }
    else if (!/[a-z]/.test(newPassword)) { setNewPwErr("Include at least one lowercase letter"); hasErr = true; }
    else if (!/[0-9]/.test(newPassword)) { setNewPwErr("Include at least one number"); hasErr = true; }
    else if (!/[^A-Za-z0-9]/.test(newPassword)) { setNewPwErr("Include at least one special character"); hasErr = true; }
    else setNewPwErr("");

    if (!confirmPassword) { setConfPwErr("Please confirm your password"); hasErr = true; }
    else if (confirmPassword !== newPassword) { setConfPwErr("Passwords do not match"); hasErr = true; }
    else setConfPwErr("");

    if (hasErr) return;

    setLoading(true);
    setFormErr("");

    try {
      const result = await secureAuthService.confirmSignInWithNewPassword(newPassword);

      if (result.success) {
        const user = {
          email: result.user.email,
          name: result.user.email.split('@')[0]
        };
        onLogin(user);
      } else {
        setFormErr(result.message || "Failed to set new password");
        setShake(true);
        setTimeout(() => setShake(false), 500);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error('Password change error:', error);
      setFormErr(error.message || "Failed to set new password");
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div className="login-page">
      {step === "login" ? (
        <form 
          onSubmit={handleSubmit}
          className={`login-card ${isMobile ? 'mobile' : ''} ${shake ? 'shake' : ''}`}
        >
          {/* Logo */}
          <img 
            src={aitLogo} 
            alt="AI Tensors" 
            className={`login-logo ${isMobile ? 'mobile' : ''}`}
          />

          {/* Title */}
          <h1 className={`login-heading ${isMobile ? 'mobile' : ''}`}>Welcome Back</h1>
          <p className="login-subheading">Sign in to AI Tensors Medical Dashboard</p>

          {/* Global error */}
          {formErr && (
            <div className="login-error-message">
              {formErr}
            </div>
          )}

          {/* Email */}
          <div className="login-form-group">
            <label className="login-label">Email Address</label>
            <div className="login-input-wrapper">
              <div className="login-input-icon">
                <MailIcon />
              </div>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, email: true }))}
                className={`login-input login-input--with-icon ${emailErr && touched.email ? 'error' : ''}`}
              />
              {emailErr && touched.email && (
                <p className="login-field-error">{emailErr}</p>
              )}
            </div>
          </div>

          {/* Password */}
          <div className="login-form-group">
            <div className="login-label-row">
              <label className="login-label">Password</label>
              <span
                onClick={onForgotPassword}
                className="login-forgot-link"
              >
                Forgot Password?
              </span>
            </div>
            <div className="login-input-wrapper">
              <div className="login-input-icon">
                <LockIcon />
              </div>
              <input
                type={showPw ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, pw: true }))}
                className={`login-input login-input--with-icon ${pwErr && touched.pw ? 'error' : ''}`}
              />
              {/* Visibility toggle */}
              <button
                type="button"
                onClick={() => setShowPw(s => !s)}
                className={`login-password-toggle ${pwErr && touched.pw ? 'with-error' : ''}`}
              >
                {showPw ? <EyeOpen /> : <EyeClosed />}
              </button>
              {pwErr && touched.pw && (
                <p className="login-field-error">{pwErr}</p>
              )}
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="login-submit-button"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>

          {/* Footer */}
          <p className="login-footer">
            &copy; {new Date().getFullYear()} AI Tensors &mdash; Medical Monitoring Platform
          </p>
        </form>
      ) : (
        <form 
          onSubmit={handleNewPasswordSubmit}
          className={`login-card ${isMobile ? 'mobile' : ''} ${shake ? 'shake' : ''}`}
        >
          {/* Logo */}
          <img 
            src={aitLogo} 
            alt="AI Tensors" 
            className={`login-logo ${isMobile ? 'mobile' : ''}`}
          />

          {/* Title */}
          <h1 className={`login-heading ${isMobile ? 'mobile' : ''}`}>Set New Password</h1>
          <p className="login-subheading">Please create a new password for your account</p>

          {/* Global error */}
          {formErr && (
            <div className="login-error-message">
              {formErr}
            </div>
          )}

          {/* New Password */}
          <div className="login-form-group">
            <label className="login-label">New Password</label>
            <div className="login-input-wrapper">
              <div className="login-input-icon">
                <LockIcon />
              </div>
              <input
                type={showNewPw ? "text" : "password"}
                placeholder="Enter new password"
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); setNewPwErr(""); }}
                className={`login-input login-input--with-icon ${newPwErr ? 'error' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowNewPw(s => !s)}
                className={`login-password-toggle ${newPwErr ? 'with-error' : ''}`}
              >
                {showNewPw ? <EyeOpen /> : <EyeClosed />}
              </button>
              {newPwErr && (
                <p className="login-field-error">{newPwErr}</p>
              )}
            </div>
          </div>

          {/* Confirm Password */}
          <div className="login-form-group">
            <label className="login-label">Confirm Password</label>
            <div className="login-input-wrapper">
              <div className="login-input-icon">
                <LockIcon />
              </div>
              <input
                type={showConfPw ? "text" : "password"}
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setConfPwErr(""); }}
                className={`login-input login-input--with-icon ${confPwErr ? 'error' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowConfPw(s => !s)}
                className={`login-password-toggle ${confPwErr ? 'with-error' : ''}`}
              >
                {showConfPw ? <EyeOpen /> : <EyeClosed />}
              </button>
              {confPwErr && (
                <p className="login-field-error">{confPwErr}</p>
              )}
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="login-submit-button"
          >
            {loading ? "Setting Password..." : "Set New Password"}
          </button>

          {/* Footer */}
          <p className="login-footer">
            &copy; {new Date().getFullYear()} AI Tensors &mdash; Medical Monitoring Platform
          </p>
        </form>
      )}
    </div>
  );
}
